/**
 * Instagram avatar content (Urban Unity) — Joey, Oct 24 2026 build; v3 (4:36 PM ET) per Joey's
 * feedback on v2: an avatar strip at the top (click any avatar to see its full Callix demographics,
 * psychographic pain points & desires, objections & triggers, and language bank — same idea as
 * opening an avatar inside Callix itself), the dashboard's own global date-range filter instead of a
 * fixed month (reels are date-filtered by posted date; matched sales calls and EOCs are NOT — "any
 * sales call"), richer Instagram metrics, a working X to close the analytics pop-up (Esc and a
 * backdrop click also close it), a combined "mega table" of matched sales calls + EOCs with an
 * All / Sales Calls / EOC toggle (every reel's matches are guaranteed to render, even when there are
 * none — "no matched calls yet" rather than a dead end), and a visibly premium UI with skeleton
 * loaders, fade-ins, hover depth and real spacing instead of the flat v2 look.
 *
 * Since organic IG carries no UTMs, every avatar tag and every call match below is INFERRED
 * (avatar-level, not reel-specific — see ig-reels.ts header) and labeled as such throughout.
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useDateRange } from "../context/DateRangeContext";

interface SharedTerm { term: string; reelQuote: string; callQuote: string; inLanguageBank: boolean }
interface FieldScore { score: number; quote: string }
interface ScoreV4 {
  topTotal: number; runnerUpKey: string | null; runnerUpTotal: number; threshold: number;
  scoreTotals: Record<string, number>;
  fieldScores: Record<string, Record<string, FieldScore>>;
}
interface BuyerLanguageData {
  phrases: string[]; pains: string[]; objections: string[];
  buyingSignal: { quote: string; why: string } | null;
}
interface SemanticPair { theme: string; reelQuote: string; callQuote: string }
interface MatchData {
  kind: "sales_call"; callKey: string; lead: string; occurredAt: string; outcome: string | null;
  cashCollected: number; matchReason: string; confidence: string; inferred: true; hasEoc: boolean;
  eocRows: EocRow[]; url: string | null; source: string | null;
  matchType?: "terminology" | "avatar_legacy"; sharedTerms?: SharedTerm[];
  callAvatarKey?: string | null; callUnclassified?: boolean; callTopTotal?: number | null;
  buyerLanguage?: BuyerLanguageData | null;
  semanticPairs?: SemanticPair[];
  semanticNoOverlap?: boolean;
}
interface EocRow {
  offerPitched: string | null; callOutcome: string | null; callNotes: string | null;
  cashCollected: number; revenueGenerated: number; paymentPlan: string | null; paymentMethod: string | null;
  mrrNumber: number | null; closerName: string | null; setterName: string | null; dateCallWasTaken: string | null;
}
interface ReelData {
  shortcode: string; url: string; postedAt: string; caption: string; durationSec: number | null;
  transcript: string; topic: string; avatarReason: string; funnelStage: string | null; format: string | null;
  hasVideo: boolean; hasThumb: boolean; likeCount: number | null; commentCount: number | null;
  viewCount: number | null; shareCount: number | null; saveCount: number | null;
  avatarKey: string | null; avatarTitle: string; avatarSubtitle: string;
  matchedCalls: MatchData[]; callsCount: number; closesCount: number; eocCount: number; cashCollected: number;
  usingLegacyFallback?: boolean; unclassified?: boolean; unclassifiedReason?: string;
  avatarKeyLegacy?: string | null; scoreV4?: ScoreV4 | null; terminology?: string[];
}
interface RollupRow { key: string; title: string; subtitle: string; reelsCount: number; reelsPct: number; callsCount: number; closesCount: number; cashCollected: number }
interface InstagramContentData {
  range: { startDate: string; endDate: string };
  totals: { reels: number; calls: number; closes: number; cashCollected: number; unmatchedCalls: number };
  rollup: RollupRow[];
  reels: ReelData[];
  unclassifiedReels: ReelData[];
  inferredNote: string;
}
interface AvatarProfile { key: string; title: string; subtitle: string; short: string; profile: Record<string, any> | null; synced: boolean; syncedAt: string | null }

const money = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v || 0);
const dateFmt = (s: string) => new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const dateFmtFull = (s: string) => new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const compactNum = (v: number | null) => v == null ? "—" : new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v);
const outcomeColor = (o: string | null) => {
  if (o === "closed") return "text-green-600 bg-green-50 border-green-200 dark:bg-green-950/40 dark:border-green-900 dark:text-green-400";
  if (o === "deposit") return "text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-400";
  if (o === "no_close") return "text-red-500 bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-900 dark:text-red-400";
  return "text-muted-foreground bg-secondary/40 border-border";
};
const humanize = (k: string) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const HeartIcon = () => (
  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21s-6.7-4.35-9.3-8.3C.8 9.8 1.7 6.4 4.6 5.1c2.1-1 4.4-.3 5.9 1.4l1.5 1.7 1.5-1.7c1.5-1.7 3.8-2.4 5.9-1.4 2.9 1.3 3.8 4.7 1.9 7.6C18.7 16.65 12 21 12 21z"/></svg>
);
const CommentIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
);
const PhoneSmallIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
);
const ReportIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
);
const CopyIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
);
const XIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.25} d="M6 18L18 6M6 6l12 12"/></svg>
);
const ExternalLinkIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
);
const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
);
const EyeIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
);
const ShareArrowIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342a4.983 4.983 0 000-2.684m0 2.684a3 3 0 11-5.368 2.684 3 3 0 015.368-2.684zm0-2.684a3 3 0 10-5.368-2.684 3 3 0 005.368 2.684zm9.632 4.316a3 3 0 10-5.368 2.684 3 3 0 005.368-2.684zm0 0a4.983 4.983 0 000-2.684m0 2.684l-6.632-3.316m6.632-5l-6.632 3.316"/></svg>
);
const BookmarkIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/></svg>
);
const UserGroupIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 2a4 4 0 10-8 0 4 4 0 008 0z"/></svg>
);

/** Shared "pill toggle" control — same visual language as the dashboard's Last Touch/First Touch
 * attribution toggle on the YouTube tab, reused here for the avatar-profile tabs and the mega-table filter. */
function PillToggle<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) {
  return (
    <div className="flex items-center rounded-lg border border-border bg-secondary/30 p-0.5 gap-0.5">
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all duration-150 whitespace-nowrap ${
            value === o.value ? "bg-[var(--brand-primary)] text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Generic renderer for Callix's nested avatar-profile JSON (demographics, psychographic_profile,
 * pain_points, desires, objections, buying_triggers, language_bank) so every field in the source
 * data shows up without hand-coding each avatar's exact schema. */
function SmartValue({ value, depth = 0 }: { value: any; depth?: number }) {
  if (value == null || value === "") return <span className="text-muted-foreground">—</span>;
  if (typeof value === "string") return <p className="text-xs leading-relaxed text-foreground/90">{value}</p>;
  if (typeof value === "number") return <span className="text-xs font-medium">{value}</span>;
  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === "string")) {
      return (
        <ul className="space-y-1 list-disc list-inside marker:text-[var(--brand-primary)]">
          {value.map((v, i) => <li key={i} className="text-xs leading-relaxed text-foreground/90">{v}</li>)}
        </ul>
      );
    }
    return (
      <div className="space-y-2">
        {value.map((v, i) => (
          <div key={i} className={`rounded-lg border border-border/60 bg-secondary/20 p-2.5 ${depth > 0 ? "" : ""}`}>
            <SmartValue value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    const entries = Object.entries(value);
    return (
      <div className={`grid gap-2 ${depth === 0 ? "sm:grid-cols-2" : ""}`}>
        {entries.map(([k, v]) => (
          <div key={k} className="space-y-0.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{humanize(k)}</p>
            <SmartValue value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-xs">{String(value)}</span>;
}

const PROFILE_TABS: { key: string; label: string; keys: string[] }[] = [
  { key: "demographics", label: "Demographics", keys: ["demographics"] },
  { key: "psychographics", label: "Psychographics · Pain Points & Desires", keys: ["psychographic_profile", "pain_points", "desires", "challenges"] },
  { key: "objections", label: "Objections & Triggers", keys: ["objections", "buying_triggers"] },
  { key: "language", label: "Language Bank", keys: ["language_bank"] },
];

/** One avatar card in the top strip — click opens the full Callix profile. */
function AvatarCard({ avatar, onOpen }: { avatar: AvatarProfile; onOpen: () => void }) {
  return (
    <motion.button
      onClick={onOpen}
      whileHover={{ y: -3 }}
      className="group shrink-0 w-[220px] text-left rounded-xl border border-border bg-gradient-to-br from-card to-secondary/30 p-3.5 shadow-sm hover:shadow-lg hover:border-[var(--brand-primary)]/40 transition-all duration-200"
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="h-8 w-8 rounded-full bg-[var(--brand-primary)]/10 flex items-center justify-center text-[var(--brand-primary)] shrink-0 group-hover:scale-105 transition-transform">
          <UserGroupIcon />
        </div>
        {!avatar.synced && <span className="text-[9px] text-amber-600 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded px-1 py-0.5">not synced</span>}
      </div>
      <p className="text-sm font-semibold leading-tight">{avatar.title}</p>
      {avatar.subtitle && <p className="text-[10px] text-muted-foreground mb-1">{avatar.subtitle}</p>}
      <p className="text-[11px] text-muted-foreground line-clamp-3 mt-1 leading-relaxed">{avatar.short}</p>
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[var(--brand-primary)] mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        Open full profile <ExternalLinkIcon />
      </span>
    </motion.button>
  );
}

/** Full-screen Callix avatar profile — demographics, psychographic pain points & desires, objections
 * & triggers, and language bank, same idea as opening the avatar directly inside Callix. */
function AvatarProfileModal({ avatar, open, onClose }: { avatar: AvatarProfile | null; open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState(PROFILE_TABS[0].key);
  if (!avatar) return null;
  const profile = avatar.profile || {};
  const activeTab = PROFILE_TABS.find((t) => t.key === tab) || PROFILE_TABS[0];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl w-[94vw] h-[88vh] p-0 gap-0 rounded-2xl overflow-hidden flex flex-col">
        <div className="shrink-0 bg-gradient-to-r from-[var(--brand-primary)]/10 to-transparent border-b border-border px-5 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-base font-bold truncate">{avatar.title}{avatar.subtitle ? ` ${avatar.subtitle}` : ""}</p>
            <p className="text-[11px] text-muted-foreground">Callix avatar · read-only</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            <XIcon />
          </button>
        </div>
        {!avatar.synced ? (
          <div className="flex-1 flex items-center justify-center p-8 text-sm text-muted-foreground text-center">
            This avatar hasn't been synced from Callix into ig_avatar_profiles yet — re-run the seed script to pull it in.
          </div>
        ) : (
          <>
            <div className="shrink-0 px-5 pt-4 pb-3 border-b border-border/60 space-y-3">
              {profile.avatar_summary && <p className="text-xs leading-relaxed text-muted-foreground">{profile.avatar_summary}</p>}
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                <PillToggle value={tab} onChange={setTab} options={PROFILE_TABS.map((t) => ({ value: t.key, label: t.label }))} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {activeTab.keys.map((k) => profile[k] ? (
                <div key={k}>
                  <p className="text-xs font-bold uppercase tracking-wide text-[var(--brand-primary)] mb-2">{humanize(k)}</p>
                  <SmartValue value={profile[k]} />
                </div>
              ) : null)}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Horizontal strip of all 6 Callix avatars at the top of the tab — click any card to open its full
 * profile (Joey: "just like the avatars are in Callix... click into any of those at any time"). */
function AvatarStrip() {
  const [avatars, setAvatars] = useState<AvatarProfile[] | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/content/instagram/avatars").then((r) => r.json()).then((j) => j.success && setAvatars(j.data)).catch(() => {});
  }, []);

  const openAvatar = useMemo(() => avatars?.find((a) => a.key === openKey) || null, [avatars, openKey]);

  if (!avatars) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-1">
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="shrink-0 w-[220px] h-[128px] rounded-xl bg-secondary/40 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        {avatars.map((a) => <AvatarCard key={a.key} avatar={a} onOpen={() => setOpenKey(a.key)} />)}
      </div>
      <AvatarProfileModal avatar={openAvatar} open={!!openKey} onClose={() => setOpenKey(null)} />
    </div>
  );
}

/** Row-of-cards grid card — mirrors the YouTube tab's card look: thumbnail, date, engagement, avatar
 * badge, # matched calls, # EOCs — with hover lift and a subtle gradient, not flat. */
function ReelGridCard({ reel, index, onOpen }: { reel: ReelData; index: number; onOpen: () => void }) {
  const thumbSrc = `/api/content/instagram/thumbnail?shortcode=${encodeURIComponent(reel.shortcode)}`;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.05, 0.5), duration: 0.35 }}>
      <Card
        className="overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 group cursor-pointer border-border/70"
        onClick={onOpen}
      >
        <div className="relative bg-black" style={{ aspectRatio: "9 / 16" }}>
          {reel.hasThumb ? (
            <img src={thumbSrc} alt={reel.caption || reel.shortcode} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No thumbnail</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3">
            <span className="text-white text-xs font-semibold px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">Open analytics ↗</span>
          </div>
          <span className="absolute top-2 left-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/60 text-white">{dateFmt(reel.postedAt)}</span>
          {reel.eocCount > 0 && <span className="absolute top-2 right-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/90 text-white">{reel.eocCount} EOC</span>}
        </div>
        <CardContent className="p-3 space-y-2.5">
          <p className="text-xs font-medium line-clamp-2 min-h-[2rem]">{reel.caption || "(no caption)"}</p>
          <div className="flex items-center gap-4 text-[11px] text-muted-foreground border-y border-border/50 py-1.5 px-0.5">
            <span className="flex items-center gap-1" title="Likes"><HeartIcon />{compactNum(reel.likeCount)}</span>
            <span className="flex items-center gap-1" title="Comments"><CommentIcon />{compactNum(reel.commentCount)}</span>
          </div>
          <div className="text-[11px] font-medium px-2 py-1 rounded-md bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] truncate" title={`${reel.avatarTitle} ${reel.avatarSubtitle}`}>
            {reel.avatarTitle}{reel.avatarSubtitle ? ` ${reel.avatarSubtitle}` : ""}
          </div>
          <div className="flex items-center justify-between text-xs pt-0.5">
            <span className="flex items-center gap-1 text-muted-foreground"><PhoneSmallIcon />{reel.callsCount} call{reel.callsCount === 1 ? "" : "s"}{reel.usingLegacyFallback && reel.callsCount > 0 ? <span className="text-[9px] ml-1 opacity-75">(avatar)</span> : null}</span>
            <span className="font-semibold text-emerald-600 dark:text-emerald-400" title={reel.usingLegacyFallback ? "Avatar history — not attributed to this reel" : undefined}>{reel.cashCollected > 0 ? money(reel.cashCollected) : "—"}</span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/** One row in the mega table — a sales call, optionally carrying EOC report(s). Tap to expand the
 * transcript (fetched on demand) and, when present, the full EOC detail. An explicit "Open in
 * Callix/Fireflies" link renders whenever the call carries a recording URL. */
function MegaTableRow({ m }: { m: MatchData }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<{ loading: boolean; error?: string; summary?: string | null; transcript?: string | null } | null>(null);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !state) {
      setState({ loading: true });
      fetch(`/api/ads/sales-call-transcript?${new URLSearchParams({ key: m.callKey })}`)
        .then((r) => r.json())
        .then((j) => setState(j.success ? { loading: false, summary: j.data.summary, transcript: j.data.transcript } : { loading: false, error: j.error || "Failed to load" }))
        .catch(() => setState({ loading: false, error: "Failed to load transcript" }));
    }
  };

  const sourceLabel = m.source === "callix" ? "Open in Callix" : m.source === "fireflies" ? "Open in Fireflies" : "Open recording";

  return (
    <div className="border-b border-border/50 last:border-0">
      <button onClick={handleToggle} className="w-full grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1.3fr_0.9fr_0.8fr_0.9fr_auto] items-center gap-2 px-3 py-2.5 text-left text-xs hover:bg-secondary/30 transition-colors">
        <span className="font-medium truncate flex items-center gap-1.5">
          {m.lead}
          {m.hasEoc && <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">EOC</span>}
        </span>
        <span className="text-muted-foreground tabular-nums hidden sm:inline">{dateFmt(m.occurredAt)}</span>
        <span className={`justify-self-start px-1.5 py-0.5 rounded border text-[10px] hidden sm:inline-block ${outcomeColor(m.outcome)}`}>{m.outcome ? m.outcome.replace(/_/g, " ") : "—"}</span>
        <span className="font-semibold tabular-nums">{m.cashCollected > 0 ? money(m.cashCollected) : "—"}</span>
        <span className="text-muted-foreground justify-self-end"><ChevronIcon open={open} /></span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="px-3 pb-3 text-xs space-y-3 bg-secondary/10">
              <div className="flex items-center justify-between gap-2 flex-wrap pt-2">
                <span className="text-[11px] text-muted-foreground italic flex items-center gap-1.5">
                  {m.matchType === "terminology" ? "Shared terminology match" : (m.matchReason || "Avatar-level match")} · {m.confidence} confidence
                  <span className="not-italic text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-border text-muted-foreground">inferred match</span>
                </span>
                {m.url && (
                  <a href={m.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-semibold text-[var(--brand-primary)] hover:underline">
                    {sourceLabel} <ExternalLinkIcon />
                  </a>
                )}
              </div>
              <SharedTermEvidence sharedTerms={m.sharedTerms} lead={m.lead} />
              <ReelCallSemanticEvidence pairs={m.semanticPairs} noOverlap={m.semanticNoOverlap} lead={m.lead} />
              <BuyerLanguage bl={m.buyerLanguage} />
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); const txt = (state?.transcript || state?.summary || ""); if (txt) { navigator.clipboard.writeText(txt); } }}
                  disabled={!state?.transcript && !state?.summary}
                  className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1 hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <CopyIcon />Copy transcript
                </button>
                <button onClick={(e) => { e.stopPropagation(); const txt = state?.transcript || state?.summary || ""; if (txt) downloadTextFile(`${m.callKey.replace(/[^a-z0-9]+/gi, "_")}.srt`, buildApproximateSrt(txt)); }}
                  disabled={!state?.transcript && !state?.summary}
                  className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1 hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  Download .srt
                </button>
                {state?.loading && <span className="text-[11px] text-muted-foreground">loading…</span>}
                {state?.error && <span className="text-[11px] text-red-500">{state.error}</span>}
                {state && !state.loading && !state.error && !state.transcript && !state.summary && <span className="text-[11px] text-muted-foreground">no transcript or summary for this call</span>}
              </div>
              {m.eocRows.map((e, i) => (
                <div key={i} className="rounded-lg border border-emerald-700 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950 p-3 space-y-1.5">
                  <p className="font-semibold text-[10px] uppercase tracking-wide text-emerald-800 dark:text-emerald-200">End-of-Call Report{m.eocRows.length > 1 ? ` #${i + 1}` : ""}</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-emerald-900 dark:text-emerald-50">
                    {e.offerPitched && <div><span className="text-emerald-800 dark:text-emerald-300">Offer: </span>{e.offerPitched}</div>}
                    {e.callOutcome && <div><span className="text-emerald-800 dark:text-emerald-300">Outcome: </span>{e.callOutcome}</div>}
                    {e.cashCollected > 0 && <div><span className="text-emerald-800 dark:text-emerald-300">Cash: </span><span className="font-semibold">{money(e.cashCollected)}</span></div>}
                    {e.mrrNumber ? <div><span className="text-emerald-800 dark:text-emerald-300">MRR: </span>{money(e.mrrNumber)}</div> : null}
                    {e.paymentPlan && <div><span className="text-emerald-800 dark:text-emerald-300">Payment plan: </span>{e.paymentPlan}</div>}
                    {e.paymentMethod && <div><span className="text-emerald-800 dark:text-emerald-300">Method: </span>{e.paymentMethod}</div>}
                    {e.closerName && <div><span className="text-emerald-800 dark:text-emerald-300">Closer: </span>{e.closerName}</div>}
                    {e.setterName && <div><span className="text-emerald-800 dark:text-emerald-300">Setter: </span>{e.setterName}</div>}
                  </div>
                  {e.callNotes && <p className="pt-1 border-t border-emerald-300 dark:border-emerald-800 mt-1.5 text-emerald-900 dark:text-emerald-50"><span className="text-emerald-800 dark:text-emerald-300">Notes: </span>{e.callNotes}</p>}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  demographics: "Demographics", desired_outcomes: "Desired outcomes", pain_points: "Pain points",
  objections_triggers: "Objections / triggers", language_bank: "Language bank",
};
const FIELD_ORDER = ["demographics", "desired_outcomes", "pain_points", "objections_triggers", "language_bank"];

/** Compact 0-3 field-score breakdown with quotes, for a reel's (or call's) best-fit avatar — and,
 * when Unclassified, the runner-up so Joey can see what almost scored. Tight by design (Joey:
 * "really condense that card"). */
function FieldScoreBreakdown({ scoreV4, unclassified, avatarTitles }: { scoreV4?: ScoreV4 | null; unclassified?: boolean; avatarTitles: Record<string, { title: string; subtitle: string }> }) {
  const [expanded, setExpanded] = useState(false);
  if (!scoreV4) return null;
  const showKey = unclassified ? (scoreV4.runnerUpKey || Object.keys(scoreV4.fieldScores)[0]) : Object.entries(scoreV4.scoreTotals).sort((a, b) => b[1] - a[1])[0]?.[0];
  const fields = showKey ? scoreV4.fieldScores[showKey] : null;
  if (!fields) return null;
  const def = avatarTitles[showKey];
  return (
    <div className="rounded-xl border border-border bg-card p-3 space-y-1.5 shadow-sm">
      <button onClick={() => setExpanded((v) => !v)} className="w-full flex items-center justify-between text-left">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Field score{unclassified ? ` — closest fit: ${def?.title || showKey}` : ""} ({scoreV4.scoreTotals[showKey]}/15)
        </span>
        <ChevronIcon open={expanded} />
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="space-y-2 pt-1">
              {FIELD_ORDER.map((fk) => {
                const fv = fields[fk];
                if (!fv) return null;
                return (
                  <div key={fk} className="text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-foreground">{FIELD_LABELS[fk] || fk}</span>
                      <span className="flex items-center gap-0.5" aria-label={`${fv.score} of 3`}>
                        {[0, 1, 2].map((i) => (
                          <span key={i} className={`h-1.5 w-1.5 rounded-full ${i < fv.score ? "bg-[var(--brand-primary)]" : "bg-border"}`} />
                        ))}
                      </span>
                    </div>
                    {fv.quote ? <p className="text-muted-foreground italic mt-0.5">&ldquo;{fv.quote}&rdquo;</p> : <p className="text-muted-foreground/60 mt-0.5">no quote — score 0</p>}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Small chip row for distinctive terminology (the reel's own terms, or shared terms on a matched
 * call). Chips carrying a quote show it as a title tooltip. */
/** Highlights the first case-insensitive occurrence of `term` inside `text`; falls back to the
 * plain text when the exact string isn't present (e.g. a de-pluralized term vs. the spoken form). */
function highlightTerm(text: string | undefined, term: string): React.ReactNode {
  if (!text) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[var(--brand-primary)]/20 text-foreground font-semibold rounded px-0.5">{text.slice(idx, idx + term.length)}</mark>
      {text.slice(idx + term.length)}
    </>
  );
}

function TermChips({ terms, label, callCounts }: { terms?: string[]; label: string; callCounts?: Record<string, number> }) {
  if (!terms || terms.length === 0) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-3 space-y-1.5 shadow-sm">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {terms.map((t, i) => {
          const count = callCounts?.[t.toLowerCase().trim()] || 0;
          return (
            <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border bg-secondary/40 text-foreground">
              {t}
              {count > 0 && (
                <span title={`Also said on ${count} matched sales call${count > 1 ? "s" : ""}`}
                  className="inline-flex items-center justify-center text-[9px] font-semibold px-1 min-w-[15px] h-[15px] rounded-full bg-[var(--brand-primary)]/20 text-foreground">
                  {count}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Visible evidence per shared term — the exact reel line and the exact call line, term
 * highlighted in both. Joey asked to SEE the quotes on open, not hover for them (so this renders
 * inline, not as a tooltip), and it has to hold up on a phone: stacked on narrow screens, side by
 * side from sm: up. */
function SharedTermEvidence({ sharedTerms, lead }: { sharedTerms?: SharedTerm[]; lead: string }) {
  if (!sharedTerms || sharedTerms.length === 0) return null;
  return (
    <div className="space-y-2">
      {sharedTerms.map((s, i) => (
        <div key={i} className="rounded-lg border border-border bg-card p-2.5 space-y-1.5">
          <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border font-medium ${s.inLanguageBank ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-foreground" : "border-border bg-secondary/40 text-foreground"}`}>
            {s.term}{s.inLanguageBank ? " ★ in language bank" : ""}
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <p className="text-[11px] leading-relaxed text-foreground">
              <span className="font-semibold text-muted-foreground">In the reel: </span>
              &ldquo;{highlightTerm(s.reelQuote, s.term)}&rdquo;
            </p>
            <p className="text-[11px] leading-relaxed text-foreground">
              <span className="font-semibold text-muted-foreground">On the call ({lead}): </span>
              &ldquo;{highlightTerm(s.callQuote, s.term)}&rdquo;
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

/** "Said in the reel" / "said on the call" pairs matched by MEANING, not exact vocabulary (Joey,
 * Oct 25 2026: "the reel says market position is everything; the buyer says I don't know where to
 * position the brand" — different words, same theme). Renders beside SharedTermEvidence (exact
 * shared terms), never in place of it — a call can have one, both, or neither signal.
 * undefined = not computed yet for this exact (reel, call) pair (still backfilling); an empty array
 * with noOverlap=true = computed, and the two genuinely don't overlap — stated plainly, not hidden. */
function ReelCallSemanticEvidence({ pairs, noOverlap, lead }: { pairs?: SemanticPair[]; noOverlap?: boolean; lead: string }) {
  if (pairs === undefined) return null; // not computed yet for this pair — say nothing rather than imply "no overlap"
  if (pairs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-secondary/20 p-2.5">
        <p className="text-[11px] text-muted-foreground italic">No shared theme found between this reel and this call yet — matched by meaning, so this is a real "no overlap," not a missing check.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Matched by meaning</p>
      {pairs.map((p, i) => (
        <div key={i} className="rounded-lg border border-border bg-card p-2.5 space-y-1.5">
          <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-foreground font-medium">
            {p.theme}
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <p className="text-[11px] leading-relaxed text-foreground">
              <span className="font-semibold text-muted-foreground">Said in the reel: </span>
              &ldquo;{p.reelQuote}&rdquo;
            </p>
            <p className="text-[11px] leading-relaxed text-foreground">
              <span className="font-semibold text-muted-foreground">Said on the call ({lead}): </span>
              &ldquo;{p.callQuote}&rdquo;
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Buyer's language, personalized to ONE sales call — same grouping as Callix's avatar Language Bank
 * (phrases they use / pains / objections & triggers), but pulled from THIS call's own transcript
 * instead of the avatar's aggregate profile (Joey, Oct 25 2026: "I want to build buyer's language
 * into each and every sales call... personalized to that sales call"). Short verbatim quotes only,
 * chip-style — never the full transcript (Copy/SRT cover that). null = no real transcript exists for
 * this call yet (most calls currently only carry a closer-written summary, which isn't the buyer's
 * own words), stated plainly rather than guessed from the summary. */
function BuyerLanguage({ bl }: { bl?: BuyerLanguageData | null }) {
  if (bl === null || bl === undefined) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-secondary/20 p-2.5">
        <p className="text-[11px] text-muted-foreground italic">No buyer's-language yet for this call — it only has a closer-written summary on file, not a full transcript to pull verbatim quotes from.</p>
      </div>
    );
  }
  const Group = ({ label, items }: { label: string; items: string[] }) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="space-y-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <div className="flex flex-wrap gap-1.5">
          {items.map((q, i) => (
            <span key={i} className="inline-block text-[11px] leading-relaxed px-2 py-1 rounded-md border border-border bg-card text-foreground">
              &ldquo;{q}&rdquo;
            </span>
          ))}
        </div>
      </div>
    );
  };
  const nothing = (!bl.phrases?.length) && (!bl.pains?.length) && (!bl.objections?.length) && !bl.buyingSignal?.quote;
  if (nothing) {
    return <p className="text-[11px] text-muted-foreground italic">Transcript read, but nothing distinctive enough to pull out for this call.</p>;
  }
  return (
    <div className="rounded-lg border border-border bg-card p-2.5 space-y-2.5">
      <p className="text-[11px] font-semibold flex items-center gap-1.5">Buyer's language <span className="not-italic text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-border text-muted-foreground">from this call's transcript</span></p>
      <Group label="Phrases they use" items={bl.phrases} />
      <Group label="Pains &amp; desires" items={bl.pains} />
      <Group label="Objections &amp; triggers" items={bl.objections} />
      {bl.buyingSignal?.quote ? (
        <div className="space-y-0.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">What moved them</p>
          <p className="text-[11px] leading-relaxed text-foreground">&ldquo;{bl.buyingSignal.quote}&rdquo;</p>
          {bl.buyingSignal.why && <p className="text-[11px] leading-relaxed text-muted-foreground italic">{bl.buyingSignal.why}</p>}
        </div>
      ) : null}
    </div>
  );
}

function downloadTextFile(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

/** No word-level timestamps are stored for sales-call transcripts, so the SRT is built as
 * approximate sequential cues (one per sentence, timed by word count at ~2.5 words/sec) and
 * labeled as such — never presented as the real timing. */
function buildApproximateSrt(text: string): string {
  const sentences = (text || "").replace(/\s+/g, " ").trim().split(/(?<=[.!?])\s+/).filter(Boolean);
  let t = 0;
  const toTs = (sec: number) => {
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = Math.floor(sec % 60), ms = Math.round((sec % 1) * 1000);
    const pad = (n: number, l = 2) => String(n).padStart(l, "0");
    return `${pad(h)}:${pad(m)}:${pad(s)},${pad(ms, 3)}`;
  };
  const lines: string[] = ["NOTE approximate timing — no word-level timestamps available, sentence-paced", ""];
  sentences.forEach((sent, i) => {
    const words = sent.split(/\s+/).filter(Boolean).length;
    const dur = Math.max(1.5, words / 2.5);
    lines.push(String(i + 1), `${toTs(t)} --> ${toTs(t + dur)}`, sent, "");
    t += dur;
  });
  return lines.join("\n");
}

type MegaFilter = "all" | "sales_calls" | "eoc";

/** The "mega table" — every matched sales call and EOC report for a reel's avatar, in one table with
 * an All / Sales Calls / EOC toggle. Every reel's matches render here, or an explicit
 * "no matched calls yet" message — never a dead end. */
function MegaTable({ matchedCalls, callsCount, closesCount, cashCollected, usingLegacyFallback }: { matchedCalls: MatchData[]; callsCount: number; closesCount: number; cashCollected: number; usingLegacyFallback?: boolean }) {
  const [filter, setFilter] = useState<MegaFilter>("all");
  const eocCount = matchedCalls.filter((m) => m.hasEoc).length;
  const filtered = filter === "eoc" ? matchedCalls.filter((m) => m.hasEoc) : matchedCalls;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-semibold">Matched sales calls &amp; EOC reports</span>
        {usingLegacyFallback ? (
          <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
            Avatar history: {callsCount} call{callsCount === 1 ? "" : "s"} · {money(cashCollected)}
            <span className="font-normal text-muted-foreground"> (not attributed to this reel)</span>
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{callsCount} call{callsCount === 1 ? "" : "s"} · {closesCount} closed · {money(cashCollected)}</span>
        )}
      </div>
      <div className="px-4 pt-2.5 pb-2 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[11px] text-muted-foreground italic">{usingLegacyFallback
          ? "No shared-terminology match for this reel yet — showing its avatar's broader call history instead. Not date-filtered. Tap a row to open it."
          : "Matched on shared terminology between this reel and the call (not date-filtered). Tap a row to open it."}</p>
        <PillToggle value={filter} onChange={setFilter} options={[
          { value: "all", label: `All (${matchedCalls.length})` },
          { value: "sales_calls", label: `Sales Calls (${matchedCalls.length})` },
          { value: "eoc", label: `EOC (${eocCount})` },
        ]} />
      </div>
      {filtered.length > 0 ? (
        <div className="divide-y divide-border/50 max-h-[420px] overflow-y-auto">
          {filtered.map((m) => <MegaTableRow key={m.callKey} m={m} />)}
        </div>
      ) : (
        <p className="px-4 py-6 text-xs text-muted-foreground italic text-center">no matched calls yet (inferred matching)</p>
      )}
    </div>
  );
}

/** Full-screen(ish) analytics pop-up for one reel: video, transcript (copy button), avatar + reason,
 * and the mega table of matched sales calls / EOC reports. Centered with a visible backdrop margin
 * (not edge-to-edge), so a click on the backdrop closes it, Esc closes it (both are Radix's default
 * Dialog behavior), and an explicit, always-visible X sits in the header — the default Close button
 * is hidden ([&>button]:hidden) because it sat behind the sticky header on v2 and had no way to be
 * clicked (Joey: "there's no way for me to X out of it right now"). */
function ReelAnalyticsModal({ reel, open, onClose, avatarTitles }: { reel: ReelData | null; open: boolean; onClose: () => void; avatarTitles: Record<string, { title: string; subtitle: string }> }) {
  const [copied, setCopied] = useState(false);
  const termCallCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const m of reel?.matchedCalls || []) {
      for (const st of m.sharedTerms || []) {
        const key = st.term.toLowerCase().trim();
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    return counts;
  }, [reel]);
  if (!reel) return null;
  const mediaSrc = `/api/content/instagram/media?shortcode=${encodeURIComponent(reel.shortcode)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reel.transcript || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-none w-screen h-[100dvh] p-0 gap-0 rounded-none overflow-hidden flex flex-col shadow-2xl [&>button]:hidden left-0 top-0 translate-x-0 translate-y-0">
        <div className="shrink-0 bg-gradient-to-r from-[var(--brand-primary)]/10 to-transparent border-b border-border px-5 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{reel.caption || reel.shortcode}</p>
            <p className="text-xs text-muted-foreground">{dateFmtFull(reel.postedAt)} · <a href={reel.url} target="_blank" rel="noreferrer" className="hover:text-foreground underline underline-offset-2">View on Instagram ↗</a></p>
          </div>
          <button onClick={onClose} aria-label="Close" className="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            <XIcon />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 max-w-6xl mx-auto w-full">
            {/* Video + transcript column */}
            <div className="space-y-4">
              <div className="rounded-xl overflow-hidden border border-border bg-black mx-auto shadow-lg" style={{ width: "100%", maxWidth: 320 }}>
                {reel.hasVideo ? (
                  <video controls preload="metadata" playsInline src={mediaSrc} className="w-full h-auto" style={{ aspectRatio: "9 / 16" }} />
                ) : (
                  <div className="flex items-center justify-center text-xs text-muted-foreground p-10" style={{ aspectRatio: "9 / 16" }}>Video not on this server</div>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground justify-center">
                <span className="flex items-center gap-1" title="Likes"><HeartIcon />{compactNum(reel.likeCount)} likes</span>
                <span className="flex items-center gap-1" title="Comments"><CommentIcon />{compactNum(reel.commentCount)} comments</span>
              </div>

              <div className="rounded-xl border border-border bg-card p-3.5 space-y-2 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Transcript</span>
                  <span className="text-[10px] text-muted-foreground">{reel.transcript ? `${reel.transcript.trim().split(/\s+/).length} words` : "none"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handleCopy} disabled={!reel.transcript}
                    className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1 hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <CopyIcon />{copied ? "Copied!" : "Copy"}
                  </button>
                  <button onClick={() => reel.transcript && downloadTextFile(`${reel.shortcode}.srt`, buildApproximateSrt(reel.transcript))} disabled={!reel.transcript}
                    className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1 hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    Download .srt
                  </button>
                </div>
              </div>
            </div>

            {/* Avatar + matched calls column */}
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-gradient-to-br from-card to-[var(--brand-primary)]/5 p-4 space-y-1.5 shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-sm font-semibold">{reel.unclassified ? "Unclassified" : reel.avatarTitle}{!reel.unclassified && reel.avatarSubtitle ? ` ${reel.avatarSubtitle}` : ""}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground">{reel.unclassified ? "below threshold — inferred" : "highest-scoring avatar — inferred"}</span>
                </div>
                {reel.unclassified && reel.unclassifiedReason && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">{reel.unclassifiedReason}</p>
                )}
                <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Topic: </span>{reel.topic || "—"}</p>
              </div>

              <FieldScoreBreakdown scoreV4={reel.scoreV4} unclassified={reel.unclassified} avatarTitles={avatarTitles} />
              <TermChips terms={reel.terminology} label="Distinctive terms in this reel" callCounts={termCallCounts} />

              <MegaTable matchedCalls={reel.matchedCalls} callsCount={reel.callsCount} closesCount={reel.closesCount} cashCollected={reel.cashCollected} usingLegacyFallback={reel.usingLegacyFallback} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RollupStrip({ rollup }: { rollup: RollupRow[] }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      <span className="text-[11px] font-medium text-muted-foreground shrink-0">By avatar (inferred):</span>
      {rollup.map((r) => (
        <motion.div key={r.key} whileHover={{ y: -2 }} className="shrink-0 rounded-lg border border-border bg-card/60 px-3 py-1.5 flex items-center gap-3 text-xs shadow-sm hover:shadow-md transition-shadow" title={r.subtitle}>
          <span className="font-medium whitespace-nowrap">{r.title}{r.subtitle ? ` ${r.subtitle}` : ""}</span>
          <span className="text-muted-foreground">{r.reelsPct}% reels</span>
          <span className="text-muted-foreground">{r.callsCount} calls</span>
          <span className="text-muted-foreground">{r.closesCount} closed</span>
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">{money(r.cashCollected)}</span>
        </motion.div>
      ))}
    </div>
  );
}

function StatCard({ label, value, color, index }: { label: string; value: string | number; color?: string; index: number }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * index }}>
      <Card className="shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className={`text-2xl font-bold ${color || ""}`}>{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function InstagramAvatarContent() {
  const { formatForApi, formatForDisplay } = useDateRange();
  const [data, setData] = useState<InstagramContentData | null>(null);
  const [error, setError] = useState("");
  const [openShortcode, setOpenShortcode] = useState<string | null>(null);
  const apiDates = formatForApi();
  const avatarTitleMap = useMemo(() => {
    const map: Record<string, { title: string; subtitle: string }> = {};
    (data?.rollup || []).forEach((r) => { map[r.key] = { title: r.title, subtitle: r.subtitle }; });
    return map;
  }, [data]);

  useEffect(() => {
    let off = false;
    setData(null); setError("");
    const params = new URLSearchParams({ startDate: apiDates.startDate, endDate: apiDates.endDate });
    fetch(`/api/content/instagram?${params}`).then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (off) return;
      if (j.success) setData(j.data); else setError(j.error || `HTTP ${r.status}`);
    }).catch((e) => !off && setError(String(e)));
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiDates.startDate, apiDates.endDate]);

  const openReel = useMemo(() => data?.reels.find((r) => r.shortcode === openShortcode) || null, [data, openShortcode]);

  return (
    <div className="space-y-6">
      {/* Avatar strip — Joey: "just like the avatars are in Callix... click into any of those at any time" */}
      <AvatarStrip />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs font-medium text-muted-foreground">
          Showing <span className="text-foreground font-semibold">{formatForDisplay()}</span> — reels are filtered by post date; matched sales calls &amp; EOCs are not (any call, any date).
        </p>
        <span className="text-[11px] text-muted-foreground max-w-md text-right">
          @chatwithmarsh reels, tagged to Callix's 6 avatars by content topic. No UTMs on organic IG — every avatar tag and call match is inferred, never tracked.
        </span>
      </div>

      {error && <div className="text-sm text-red-500 px-1">Couldn't load Instagram content: {error}</div>}
      {!data && !error && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-[76px] rounded-xl bg-secondary/30 animate-pulse" />)}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
              <div key={i} className="rounded-xl bg-secondary/30 animate-pulse overflow-hidden">
                <div style={{ aspectRatio: "9/16" }} className="bg-secondary/50" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-secondary/60 rounded w-5/6" />
                  <div className="h-3 bg-secondary/60 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AnimatePresence>
        {data && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard index={0} label="Reels in range" value={data.totals.reels} />
              <StatCard index={1} label="Sales calls in range" value={data.totals.calls} />
              <StatCard index={2} label="Closes in range" value={data.totals.closes} />
              <StatCard index={3} label="Cash collected (by call date)" value={money(data.totals.cashCollected)} />
              <StatCard index={4} label="Calls w/ no recording to match" value={data.totals.unmatchedCalls} color="text-amber-600 dark:text-amber-400" />
            </div>

            <RollupStrip rollup={data.rollup} />

            <div className="text-xs text-muted-foreground bg-secondary/20 border border-border/50 rounded-lg px-3 py-2">
              {data.inferredNote}
            </div>

            {data.reels.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
                {data.reels.map((r, i) => (
                  <ReelGridCard key={r.shortcode} reel={r} index={i} onOpen={() => setOpenShortcode(r.shortcode)} />
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center p-10 rounded-xl border border-dashed border-border bg-card/50">
                <p className="text-sm text-muted-foreground">No Instagram reels posted in this date range.</p>
              </div>
            )}

            <ReelAnalyticsModal reel={openReel} open={!!openShortcode} onClose={() => setOpenShortcode(null)} avatarTitles={avatarTitleMap} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
